import { useEffect, useRef } from 'react'
import { lessonsApi, useCreateLessonMutation } from '@/features/editor/lessonsApi'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { selectLesson, setBlockSheetOpen, setSaveStatus } from '@/features/editor/editorSlice'
import { computeNextOrder } from '@/features/editor/lib/computeNextOrder'
import { seedLangFor } from './seedContentLang'
import type { CourseWithoutBlocks } from '@/types/course'
import type { LessonCreate } from '@/types/lesson'
import { toast } from 'sonner'
import { messagesNow } from '@/i18n'
import { messages } from './messages'

// Плейсхолдер имени кладём в слот ЯЗЫКА ОРИГИНАЛА курса, а не всегда в русский:
// у англоязычного курса (sourceLang: 'en') базовый (русский) слот у бэка `null`,
// и заполнить его — значит выдать урок за уже переведённый (см. CLAUDE.md,
// находка 1). Базовый слот при этом остаётся пустой строкой, а не null — тип
// `LessonBase.name` не nullable.
const buildLessonBody = (course: CourseWithoutBlocks): LessonCreate => {
  const n = course.lessons.length + 1
  const isEnSource = seedLangFor(course) === 'en'
  return {
    order: computeNextOrder(course.lessons),
    name: isEnSource ? '' : `Урок ${n}`,
    nameEn: isEnSource ? `Lesson ${n}` : null,
    description: null,
    descriptionEn: null,
    image: null,
    imageEn: null,
    reward: 0,
    submoduleNumber: null,
    submoduleTitle: null,
    submoduleTitleEn: null,
    type: 'mixed',
    inventoryItemId: null,
    blocks: [],
  }
}

export const useLessonCreator = (
  course: CourseWithoutBlocks,
  setExpandedLessonId: (id: number | null) => void,
) => {
  const dispatch = useAppDispatch()
  const [createLesson, { isLoading: isCreatingLesson }] = useCreateLessonMutation()

  // Пока сервер создаёт урок, автор может работать дальше: куда он ушёл, оттуда не уводим.
  const selectedLessonId = useAppSelector((s) => s.editor.selectedLessonId)
  const selectedBlockId = useAppSelector((s) => s.editor.selectedBlockId)
  const place = `${selectedLessonId}/${selectedBlockId}`
  const placeRef = useRef(place)
  useEffect(() => {
    placeRef.current = place
  }, [place])

  const addLesson = async () => {
    const startedAt = placeRef.current
    try {
      dispatch(setSaveStatus('saving'))
      const created = await createLesson({
        courseId: course.id,
        body: buildLessonBody(course),
      }).unwrap()
      // Новый урок пуст — его лента известна без запроса, без серой заглушки.
      void dispatch(
        lessonsApi.util.upsertQueryData('getLessonBlocks', created.id, created.blocks ?? []),
      )
      if (placeRef.current === startedAt) {
        dispatch(selectLesson(created.id))
        setExpandedLessonId(created.id)
        // Телефон/планшет (уже `lg`): новый урок сразу открывается шторкой настроек —
        // назвать его можно, не ища, где у урока настройки.
        if (!window.matchMedia('(min-width: 1024px)').matches) dispatch(setBlockSheetOpen(true))
      }
      dispatch(setSaveStatus('saved'))
    } catch {
      dispatch(setSaveStatus('error'))
      toast.error(messagesNow(messages).createLessonFailed)
    }
  }

  return { addLesson, isCreatingLesson }
}
